import type {FC} from 'react'
import {IconButton, Truncate} from '@primer/react'
import {PencilIcon, ReplyIcon, TrashIcon} from '@primer/octicons-react'
import {RuleModalState, type Rule, type RuleSchema} from '../../types/rules-types'
import {generateMetadataDescription} from '../../helpers/rule-schema'

import styles from './MetadataPatternRuleRow.module.css'

interface MetadataPatternRuleRowProps {
  rule: Rule
  ruleSchema: RuleSchema
  readOnly: boolean
  removeRule: (rule: Rule) => void
  restoreRule: (rule: Rule) => void
  setRuleModalState: (rule: Rule, modalState: RuleModalState) => void
}

export const MetadataPatternRuleRow: FC<MetadataPatternRuleRowProps> = ({
  rule,
  ruleSchema,
  readOnly,
  removeRule,
  restoreRule,
  setRuleModalState,
}) => {
  const {pattern} = rule.parameters

  let description = rule.parameters.name as string | undefined

  if (!description) {
    description = generateMetadataDescription(rule, ruleSchema)
  }

  return (
    <>
      <div className={styles.MetadataRuleMainContainer}>
        <div className={styles.MetadataRuleInnerContainer}>
          <label className={styles.MetadataRuleLabelContainer}>
            <span className={styles.MetadataRuleDescriptionContainer}>{description}</span>
            <Truncate expandable title={pattern as string} className={styles.MetadataRulePatternBadge}>
              <span className={styles.MetadataRulePatternText}>{pattern as string | undefined}</span>
            </Truncate>
          </label>
        </div>
      </div>
      {readOnly ? null : (
        <div className={styles.MetadataRuleActionsContainer}>
          {rule._enabled ? (
            <>
              <IconButton
                type="button"
                icon={PencilIcon}
                aria-label="Edit this metadata rule"
                size="small"
                variant="invisible"
                onClick={() => {
                  setRuleModalState(rule, RuleModalState.EDITING)
                }}
                className={styles.MetadataRuleActionsContainer}
              />
              <IconButton
                icon={TrashIcon}
                type="button"
                size="small"
                variant="invisible"
                onClick={() => removeRule(rule)}
                aria-label="Delete this metadata rule"
                className={styles.MetadataRuleDeleteButton}
              />
            </>
          ) : (
            <IconButton
              type="button"
              icon={ReplyIcon}
              aria-label="Restore this metadata rule"
              size="small"
              variant="invisible"
              onClick={() => restoreRule(rule)}
              className={styles.MetadataRuleActionsContainer}
            />
          )}
        </div>
      )}
    </>
  )
}

try{ MetadataPatternRuleRow.displayName ||= 'MetadataPatternRuleRow' } catch {}